import React from 'react';
import { ImageIcon, Wand2, X, Info, Loader2, Sparkles } from 'lucide-react';
import { InfoTooltip } from './InfoTooltip';

interface ImageSelectorProps {
  imagePreview: string;
  generateImage: boolean;
  modifyImage: boolean;
  fullModifyImage: boolean;
  isUploading?: boolean;
  selectedPlatform?: string;
  onImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onGenerateImageToggle: () => void;
  onModifyImageToggle: () => void;
  onFullModifyImageToggle: () => void;
  onImageRemove: () => void;
}

export const ImageSelector: React.FC<ImageSelectorProps> = ({
  imagePreview,
  generateImage,
  modifyImage,
  fullModifyImage,
  isUploading = false,
  selectedPlatform,
  onImageChange,
  onGenerateImageToggle,
  onModifyImageToggle,
  onFullModifyImageToggle,
  onImageRemove,
}) => {
  const isInstagram = selectedPlatform === 'instagram' || selectedPlatform === 'instagram_direct';
  const hasImage = imagePreview || generateImage;

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          <span>Image Options</span>
          <InfoTooltip
            text="Generate Image: AI creates image from your content. Modify Image: AI enhances your uploaded image. Full Modify: Comprehensive AI modifications. Upload: Use your own image."
            position="bottom"
          />
          {isInstagram && (
            <span className="text-red-500 font-semibold">*Required</span>
          )}
        </label>
        {isInstagram && !hasImage && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700 flex items-center gap-2">
              <Info className="w-4 h-4 flex-shrink-0" />
              <span>Instagram requires an image. Please upload one or enable AI generation.</span>
            </p>
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={onGenerateImageToggle}
            disabled={isUploading}
            className={`
              flex items-center justify-center gap-2 px-4 py-2 rounded-lg border w-full sm:w-auto
              ${generateImage ? 'bg-purple-600 text-white border-purple-600 hover:bg-purple-700' : 'border-gray-300 hover:bg-gray-50'}
              ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <Wand2 className="w-5 h-5" />
            <span>Generate Image with AI</span>
          </button>

          <label className={`
            flex items-center justify-center gap-2 px-4 py-2 rounded-lg border cursor-pointer w-full sm:w-auto
            ${!generateImage && imagePreview ? 'bg-blue-50 border-blue-500' : 'border-gray-300'}
            ${generateImage || isUploading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}
          `}>
            {isUploading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <ImageIcon className="w-5 h-5" />
                <span>Upload Image</span>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onImageChange}
              disabled={generateImage || isUploading}
            />
          </label>
        </div>

        {imagePreview && !generateImage && (
          <div className="mt-4 relative inline-block">
            <img
              src={imagePreview}
              alt="Preview"
              className="h-32 w-auto object-cover rounded-lg"
            />
            <button
              type="button"
              onClick={onImageRemove}
              disabled={isUploading}
              className={`
                absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600
                ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {imagePreview && !generateImage && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              AI Image Modification
            </label>
            <div className="flex items-center gap-1 text-sm text-gray-500">
              <Info className="w-4 h-4" />
              <span>Auto-cropped to 1080x1080px</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={onModifyImageToggle}
              disabled={isUploading || (fullModifyImage && !modifyImage)}
              className={`
                flex items-center justify-center gap-2 px-4 py-2 rounded-lg border w-full sm:w-auto
                ${modifyImage ? 'bg-purple-600 text-white border-purple-600 hover:bg-purple-700' : 'border-gray-300 hover:bg-gray-50'}
                ${isUploading || (fullModifyImage && !modifyImage) ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <Wand2 className="w-5 h-5" />
              <span>Overlay on AI Background</span>
            </button>
            <button
              type="button"
              onClick={(e) => {
                console.log('[ImageSelector] Full AI Transformation clicked - EVENT:', e);
                console.log('[ImageSelector] Current state - modifyImage:', modifyImage, 'fullModifyImage:', fullModifyImage);
                console.log('[ImageSelector] isUploading:', isUploading);
                console.log('[ImageSelector] Disabled calculation:', isUploading || (modifyImage && !fullModifyImage));
                console.log('[ImageSelector] Calling onFullModifyImageToggle...');
                onFullModifyImageToggle();
                console.log('[ImageSelector] onFullModifyImageToggle called!');
              }}
              disabled={isUploading || (modifyImage && !fullModifyImage)}
              className={`
                flex items-center justify-center gap-2 px-4 py-2 rounded-lg border w-full sm:w-auto
                ${fullModifyImage ? 'bg-purple-600 text-white border-purple-600 hover:bg-purple-700' : 'border-gray-300 hover:bg-gray-50'}
                ${isUploading || (modifyImage && !fullModifyImage) ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <Sparkles className="w-5 h-5" />
              <span>Full AI Transformation</span>
            </button>
          </div>
          {modifyImage && (
            <p className="mt-2 text-sm text-gray-500">
              Your image will be overlaid on an AI-generated background with transparent edges (1080x1080px).
            </p>
          )}
          {fullModifyImage && (
            <p className="mt-2 text-sm text-purple-600 font-medium">
              Your uploaded image will be completely transformed by AI while maintaining the subject.
            </p>
          )}
        </div>
      )}
    </div>
  );
};